import { components as c } from "@/components";
import EducationFormFields from "@/features/events/features/educations/EducationFormFields";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

// Dedicated schema for education form
const educationFormSchema = z.object({
	education_name: z
		.string()
		.min(1, "Please provide a name for this education session"),
	education_description: z
		.string()
		.min(1, "Please describe what was discussed or taught"),
	educated_by: z.string().uuid("Please select who provided the education"),
});

type EducationFormValues = z.infer<typeof educationFormSchema>;

const EducationFormFieldsPage = () => {
	const {
		organisation_id,
		education_name,
		education_description,
		educated_by,
		setEducationName,
		setEducationDescription,
		setEducatedBy,
	} = useTemplateStore();

	const {
		markStepComplete,
		nextStep,
		setCurrentStep,
		getNextStepRoute,
		getPreviousStepRoute,
		currentStep,
	} = useTemplateNavigationStore();
	const navigate = useNavigate();
	const { templateId } = Route.useParams();

	useEffect(() => {
		setCurrentStep("education");
	}, [setCurrentStep]);

	const form = useForm<EducationFormValues>({
		resolver: zodResolver(educationFormSchema),
		defaultValues: {
			education_name: education_name ?? "",
			education_description: education_description ?? "",
			educated_by: educated_by ?? undefined,
		},
	});

	const handleNext = (formData: EducationFormValues) => {
		setEducationName(formData.education_name);
		setEducationDescription(formData.education_description);
		setEducatedBy(formData.educated_by);

		markStepComplete(currentStep);
		nextStep();
		const nextRoute = getNextStepRoute(currentStep, { templateId });
		if (nextRoute) navigate(nextRoute);
	};

	const handlePrevious = () => {
		const prevRoute = getPreviousStepRoute(currentStep, { templateId });
		if (prevRoute) navigate(prevRoute);
	};

	return (
		<c.ui.Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleNext)}
				className="space-y-6"
			>
				<EducationFormFields organisationId={organisation_id} />

				{/* Navigation Buttons */}
				<div className="flex justify-between gap-4 mt-8">
					<c.ui.Button
						type="button"
						variant="outline"
						onClick={handlePrevious}
					>
						Previous
					</c.ui.Button>

					<c.ui.Button type="submit">Next</c.ui.Button>
				</div>
			</form>
		</c.ui.Form>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$templateId/events/education/",
)({
	component: EducationFormFieldsPage,
});
